/**
=========================================================
* Politique de Remboursement - expressiontcf.com
=========================================================

Dernière mise à jour : 06 mai 2026
=========================================================
*/

import { Box, Container, Typography, Paper, Divider, Grid, Chip, Link } from "@mui/material";
import OfficialHeader from "components/OfficialHeader";
import OfficialFooter from "components/OfficialFooter";

function RefundPolicy() {
  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <OfficialHeader />
      
      <Box
        sx={{
          backgroundColor: "#f8fafc",
          py: 8,
          flex: 1,
        }}
      >
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: 8,
              borderRadius: "8px",
              backgroundColor: "#ffffff",
              boxShadow: "0px 4px 6px -1px rgba(0,0,0,0.1), 0px 2px 4px -1px rgba(0,0,0,0.06)",
            }}
          >
            <Box sx={{ mb: 6 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: "48px",
                  fontWeight: 700,
                  lineHeight: 1.25,
                  color: "#212529",
                  mb: 3,
                  textAlign: "center",
                }}
              >
                Politique de retour et de remboursement
              </Typography>
              
              <Typography
                variant="h6"
                sx={{
                  fontSize: "20px",
                  fontWeight: 400,
                  lineHeight: 1.625,
                  color: "#7b809a",
                  textAlign: "center",
                  mb: 2,
                }}
              >
                expressiontcf.com
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  fontSize: "16px",
                  fontWeight: 300,
                  lineHeight: 1.6,
                  color: "#7b809a",
                  textAlign: "center",
                }}
              >
                Dernière mise à jour : 01 mai 2026
              </Typography>
            </Box>

            <Divider sx={{ my: 6 }} />

            <Box sx={{ mb: 6 }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: "24px",
                  fontWeight: 700,
                  lineHeight: 1.375,
                  color: "#212529",
                  mb: 3,
                }}
              >
                1. Objet
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: 1.625,
                  color: "#7b809a",
                  mb: 2,
                }}
              >
                La présente politique de retour et de remboursement a pour objectif de définir les conditions dans lesquelles les utilisateurs de la plateforme expressiontcf.com peuvent solliciter un remboursement après l’achat de crédits.
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: 1.625,
                  color: "#7b809a",
                }}
              >
                expressiontcf.com est une plateforme de simulation d’expressions écrites et orales assistées par intelligence artificielle, fonctionnant exclusivement sur un système de crédits d’utilisation, et non sur une durée d’abonnement.
              </Typography>
            </Box>

            <Box sx={{ mb: 6 }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: "24px",
                  fontWeight: 700,
                  lineHeight: 1.375,
                  color: "#212529",
                  mb: 3,
                }}
              >
                2. Fonctionnement des offres
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: 1.625,
                  color: "#7b809a",
                  mb: 2,
                }}
              >
                Les services sont proposés sous forme de packs :
              </Typography>
              
              <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
                <Chip
                  label="Bronze Plus"
                  color="primary"
                  sx={{
                    backgroundColor: "#f0f2f5",
                    color: "#7b809a",
                    fontWeight: 600,
                    fontSize: "14px",
                    px: 2,
                    py: 1,
                  }}
                />
                <Chip
                  label="Silver Plus"
                  color="primary"
                  sx={{
                    backgroundColor: "#f0f2f5",
                    color: "#7b809a",
                    fontWeight: 600,
                    fontSize: "14px",
                    px: 2,
                    py: 1,
                  }}
                />
                <Chip
                  label="Gold Plus"
                  color="primary"
                  sx={{
                    backgroundColor: "#f0f2f5",
                    color: "#7b809a",
                    fontWeight: 600,
                    fontSize: "14px",
                    px: 2,
                    py: 1,
                  }}
                />
              </Box>
              
              <Typography
                variant="body2"
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: 1.625,
                  color: "#7b809a",
                  mb: 2,
                }}
              >
                Chaque pack donne droit à un nombre défini de crédits permettant d’accéder aux fonctionnalités suivantes :
              </Typography>
              
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: 1.625,
                    color: "#7b809a",
                    mb: 1,
                  }}
                >
                  Simulations d’expression écrite
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: 1.625,
                    color: "#7b809a",
                    mb: 1,
                  }}
                >
                  Simulations d’expression orale
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: 1.625,
                    color: "#7b809a",
                    mb: 1,
                  }}
                >
                  Évaluation et correction automatisées par intelligence artificielle
                </Typography>
              </Box>
              
              <Typography
                variant="body2"
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: 1.625,
                  color: "#7b809a",
                }}
              >
                Les crédits sont débités à chaque utilisation du service.
              </Typography>
            </Box>

            <Box sx={{ mb: 6 }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: "24px",
                  fontWeight: 700,
                  lineHeight: 1.375,
                  color: "#212529",
                  mb: 3,
                }}
              >
                3. Nature des crédits
              </Typography>
              
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: 1.625,
                    color: "#7b809a",
                    mb: 1,
                  }}
                >
                  Ils sont personnels et non transférables
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: 1.625,
                    color: "#7b809a",
                    mb: 1,
                  }}
                >
                  Ils sont non remboursables après utilisation
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: 1.625,
                    color: "#7b809a",
                    mb: 1,
                  }}
                >
                  Ils n’ont pas de durée d’expiration
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: 1.625,
                    color: "#7b809a",
                  }}
                >
                  Ils sont consommés en fonction de l’usage du simulateur
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 6 }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: "24px",
                  fontWeight: 700,
                  lineHeight: 1.375,
                  color: "#212529",
                  mb: 3,
                }}
              >
                4. Conditions de remboursement
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  fontSize: "20px",
                  fontWeight: 700,
                  lineHeight: 1.375,
                  color: "#212529",
                  mb: 2,
                }}
              >
                4.1 Délai de rétractation
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: 1.625,
                  color: "#7b809a",
                  mb: 2,
                }}
              >
                L’utilisateur dispose d’un délai de 3 (trois) jours calendaires à compter de la date d’achat pour soumettre une demande de remboursement.
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  fontSize: "20px",
                  fontWeight: 700,
                  lineHeight: 1.375,
                  color: "#212529",
                  mb: 2,
                }}
              >
                4.2 Cas éligibles
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: 1.625,
                  color: "#7b809a",
                  mb: 2,
                }}
              >
                Une demande de remboursement peut être acceptée uniquement dans les cas suivants :
              </Typography>
              
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: 1.625,
                    color: "#7b809a",
                    mb: 1,
                  }}
                >
                  Aucun crédit n’a été utilisé après l’achat
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: 1.625,
                    color: "#7b809a",
                    mb: 1,
                  }}
                >
                  La demande est effectuée dans le délai de 3 jours
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: 1.625,
                    color: "#7b809a",
                  }}
                >
                  Un dysfonctionnement technique majeur empêche totalement l’accès au service
                </Typography>
              </Box>

              <Typography
                variant="h5"
                sx={{
                  fontSize: "20px",
                  fontWeight: 700,
                  lineHeight: 1.375,
                  color: "#212529",
                  mb: 2,
                }}
              >
                4.3 Cas non éligibles
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: 1.625,
                  color: "#7b809a",
                  mb: 2,
                }}
              >
                Aucun remboursement ne sera accordé dans les cas suivants :
              </Typography>
              
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: 1.625,
                    color: "#7b809a",
                    mb: 1,
                  }}
                >
                  Utilisation partielle ou totale des crédits
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: 1.625,
                    color: "#7b809a",
                    mb: 1,
                  }}
                >
                  Demande effectuée après expiration du délai de 3 jours
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: 1.625,
                    color: "#7b809a",
                    mb: 1,
                  }}
                >
                  Mauvaise compréhension du fonctionnement des crédits
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: 1.625,
                    color: "#7b809a",
                    mb: 1,
                  }}
                >
                  Insatisfaction liée à l'expérience utilisateur sans défaut technique avéré
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: 1.625,
                    color: "#7b809a",
                  }}
                >
                  Violation des conditions générales d'utilisation
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 6 }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: "24px",
                  fontWeight: 700,
                  lineHeight: 1.375,
                  color: "#212529",
                  mb: 3,
                }}
              >
                5. Procédure de demande
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: 1.625,
                  color: "#7b809a",
                  mb: 2,
                }}
              >
                Toute demande de remboursement doit être adressée à :
              </Typography>
              
              <Box
                sx={{
                  p: 4,
                  backgroundColor: "#f0f2f5",
                  borderRadius: "8px",
                  mb: 3,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "20px",
                    fontWeight: 700,
                    lineHeight: 1.625,
                    color: "#212529",
                    mb: 2,
                  }}
                >
                  📧 info@reussir-tcfcanada.com
                </Typography>
                
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: 1.625,
                    color: "#7b809a",
                    mb: 2,
                  }}
                >
                  expressiontcf.com est un service proposé par la plateforme Réussir TCF Canada.
                </Typography>
                
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: 1.625,
                    color: "#7b809a",
                    mb: 2,
                  }}
                >
                  Toutes les demandes de support et de remboursement sont traitées via cette adresse.
                </Typography>
                
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: 1.625,
                    color: "#7b809a",
                    mb: 2,
                  }}
                >
                  La demande doit obligatoirement contenir :
                </Typography>
                
                <Box component="ul" sx={{ pl: 3 }}>
                  <Typography
                    component="li"
                    variant="body2"
                    sx={{
                      fontSize: "16px",
                      fontWeight: 400,
                      lineHeight: 1.625,
                      color: "#7b809a",
                      mb: 1,
                    }}
                  >
                    L’adresse email utilisée lors de l’inscription
                  </Typography>
                  <Typography
                    component="li"
                    variant="body2"
                    sx={{
                      fontSize: "16px",
                      fontWeight: 400,
                      lineHeight: 1.625,
                      color: "#7b809a",
                      mb: 1,
                    }}
                  >
                    Le pack acheté (Bronze Plus, Silver Plus ou Gold Plus)
                  </Typography>
                  <Typography
                    component="li"
                    variant="body2"
                    sx={{
                      fontSize: "16px",
                      fontWeight: 400,
                      lineHeight: 1.625,
                      color: "#7b809a",
                      mb: 1,
                    }}
                  >
                    La date d’achat
                  </Typography>
                  <Typography
                    component="li"
                    variant="body2"
                    sx={{
                      fontSize: "16px",
                      fontWeight: 400,
                      lineHeight: 1.625,
                      color: "#7b809a",
                    }}
                  >
                    Le motif détaillé de la demande
                  </Typography>
                </Box>
                
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 300,
                    lineHeight: 1.25,
                    color: "#7b809a",
                    mt: 2,
                  }}
                >
                  Toute demande incomplète pourra ne pas être traitée.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 6 }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: "24px",
                  fontWeight: 700,
                  lineHeight: 1.375,
                  color: "#212529",
                  mb: 3,
                }}
              >
                6. Traitement des demandes
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: 1.625,
                  color: "#7b809a",
                  mb: 2,
                }}
              >
                Les demandes sont examinées dans un délai de 3 à 7 jours ouvrables à compter de leur réception.
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: 1.625,
                  color: "#7b809a",
                }}
              >
                expressiontcf.com se réserve le droit d’accepter ou de refuser toute demande après vérification des conditions d’éligibilité.
              </Typography>
            </Box>

            <Box sx={{ mb: 6 }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: "24px",
                  fontWeight: 700,
                  lineHeight: 1.375,
                  color: "#212529",
                  mb: 3,
                }}
              >
                7. Modalités de remboursement
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: 1.625,
                  color: "#7b809a",
                  mb: 2,
                }}
              >
                En cas d’acceptation :
              </Typography>
              
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: 1.625,
                    color: "#7b809a",
                    mb: 1,
                  }}
                >
                  Le remboursement sera effectué via le moyen de paiement initial
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: 1.625,
                    color: "#7b809a",
                  }}
                >
                  Le délai de traitement bancaire peut varier entre 5 et 10 jours ouvrables
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 6 }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: "24px",
                  fontWeight: 700,
                  lineHeight: 1.375,
                  color: "#212529",
                  mb: 3,
                }}
              >
                8. Problèmes techniques
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: 1.625,
                  color: "#7b809a",
                  mb: 2,
                }}
              >
                En cas de problème technique :
              </Typography>
              
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: 1.625,
                    color: "#7b809a",
                    mb: 1,
                  }}
                >
                  L’utilisateur est invité à contacter le support dans les plus brefs délais
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: 1.625,
                    color: "#7b809a",
                    mb: 1,
                  }}
                >
                  Une solution corrective sera prioritairement proposée
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: 1.625,
                    color: "#7b809a",
                  }}
                >
                  Si nécessaire, une compensation en crédits pourra être accordée
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 6 }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: "24px",
                  fontWeight: 700,
                  lineHeight: 1.375,
                  color: "#212529",
                  mb: 3,
                }}
              >
                9. Modification de la politique
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: 1.625,
                  color: "#7b809a",
                  mb: 2,
                }}
              >
                expressiontcf.com se réserve le droit de modifier la présente politique à tout moment, afin de l’adapter aux évolutions du service ou aux exigences légales.
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: 1.625,
                  color: "#7b809a",
                }}
              >
                Les modifications prennent effet dès leur publication sur le site.
              </Typography>
            </Box>

            <Box sx={{ mb: 6 }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: "24px",
                  fontWeight: 700,
                  lineHeight: 1.375,
                  color: "#212529",
                  mb: 3,
                }}
              >
                10. Contact
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: 1.625,
                  color: "#7b809a",
                  mb: 2,
                }}
              >
                Pour toute question relative à cette politique :
              </Typography>
              
              <Box
                sx={{
                  p: 4,
                  backgroundColor: "#f0f2f5",
                  borderRadius: "8px",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "20px",
                    fontWeight: 700,
                    lineHeight: 1.625,
                    color: "#212529",
                    mb: 2,
                  }}
                >
                  📧 info@reussir-tcfcanada.com
                </Typography>
                
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: 1.625,
                    color: "#7b809a",
                  }}
                >
                  expressiontcf.com est un service proposé par la plateforme Réussir TCF Canada.
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 6 }} />

            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: 1.625,
                  color: "#7b809a",
                }}
              >
                expressiontcf.com - Réussir TCF Canada
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
      
      <OfficialFooter />
    </Box>
  );
}

export default RefundPolicy;
